import { ReactNode, useState } from 'react';
import { Menu, Moon, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import { calculatorRoutes, staticContentLinks } from '@/routes';
import { useTheme } from '@/theme/theme';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';

const primaryCalculators = calculatorRoutes.filter((route) => route.title.includes('FIRE'));
const overflowCalculators = calculatorRoutes.filter((route) => !route.title.includes('FIRE'));

const desktopLinkClass =
  'inline-flex shrink-0 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground';

function toCanonicalHref(path: string): string {
  return path === '/' || path.endsWith('/') ? path : `${path}/`;
}

type DesktopDropdownProps = {
  label: string;
  children: ReactNode;
};

function DesktopDropdown({ label, children }: DesktopDropdownProps) {
  return (
    <NavigationMenu className="flex-none">
      <NavigationMenuList className="flex-none space-x-0">
        <NavigationMenuItem>
          <NavigationMenuTrigger className="shrink-0 whitespace-nowrap">{label}</NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid w-[320px] gap-1 p-2">{children}</ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

export function SiteHeader() {
  const { theme, toggleTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background shadow-sm">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-3 md:px-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/brand/logo.png" alt="" aria-hidden="true" className="h-8 w-8 object-contain block dark:hidden" />
          <img src="/brand/logo-inverse.png" alt="" aria-hidden="true" className="h-8 w-8 object-contain hidden dark:block" />
          <span className="font-display text-xl font-bold tracking-tight text-foreground">Fire-Calc</span>
        </Link>

        <nav className="hidden lg:flex lg:items-center lg:gap-1">
          {primaryCalculators.map((route) => (
            <Link key={route.path} to={toCanonicalHref(route.path)} className={desktopLinkClass}>
              {route.navLabel ?? route.title.replace(' Calculator', '')}
            </Link>
          ))}

          <DesktopDropdown label="More Calculators">
            {overflowCalculators.map((route) => (
              <li key={route.path}>
                <NavigationMenuLink asChild>
                  <Link to={toCanonicalHref(route.path)} className="block rounded-md px-2 py-2 text-sm text-foreground transition hover:bg-secondary">
                    {route.navLabel ?? route.title}
                  </Link>
                </NavigationMenuLink>
              </li>
            ))}
          </DesktopDropdown>

          <DesktopDropdown label="Guides">
            {staticContentLinks.guides.map((link) => (
              <li key={link.href}>
                <NavigationMenuLink asChild>
                  <Link
                    to={link.href}
                    className={cn(
                      'block rounded-md px-2 py-2 text-sm text-foreground transition hover:bg-secondary',
                      link.href === '/guides/' ? 'font-semibold' : ''
                    )}
                  >
                    {link.label}
                  </Link>
                </NavigationMenuLink>
              </li>
            ))}
          </DesktopDropdown>

          <DesktopDropdown label="Resources">
            {[...staticContentLinks.about, ...staticContentLinks.resources].map((link) => (
              <li key={link.href}>
                <NavigationMenuLink asChild>
                  <Link to={link.href} className="block rounded-md px-2 py-2 text-sm text-foreground transition hover:bg-secondary">
                    {link.label}
                  </Link>
                </NavigationMenuLink>
              </li>
            ))}
          </DesktopDropdown>
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 rounded-full border border-border bg-card/80 px-3 py-1.5 sm:flex">
            {theme === 'dark' ? (
              <Moon className="h-4 w-4 text-amber-300" aria-hidden="true" />
            ) : (
              <Sun className="h-4 w-4 text-amber-600" aria-hidden="true" />
            )}
            <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} ariaLabel="Toggle dark mode" />
          </div>

          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger className={cn(navigationMenuTriggerStyle(), 'lg:hidden')} aria-label="Open site navigation">
              <Menu className="h-4 w-4" />
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Calculator Navigation</SheetTitle>
                <SheetDescription>Quick access to calculators, guides, and core reference pages.</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6 pb-6">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Calculators</p>
                  <div className="grid gap-2">
                    {calculatorRoutes.map((route) => (
                      <Link
                        key={route.path}
                        to={toCanonicalHref(route.path)}
                        className="rounded-md border border-border bg-background/80 px-3 py-2 text-sm text-foreground transition hover:bg-secondary"
                        onClick={() => setMobileNavOpen(false)}
                      >
                        {route.navLabel ?? route.title}
                      </Link>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Guides</p>
                  <div className="grid gap-2">
                    {staticContentLinks.guides.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={cn(
                          'rounded-md border border-border bg-background/80 px-3 py-2 text-sm text-foreground transition hover:bg-secondary',
                          link.href === '/guides/' ? 'font-semibold' : ''
                        )}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Resources</p>
                  <div className="grid gap-2">
                    {[...staticContentLinks.about, ...staticContentLinks.resources].map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        className="rounded-md border border-border bg-background/80 px-3 py-2 text-sm text-foreground transition hover:bg-secondary"
                        onClick={() => setMobileNavOpen(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-card/70 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-foreground">Dark mode</span>
                    <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} ariaLabel="Toggle dark mode" />
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
            <Link to="/changelog/">Changelog</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}
